import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSendLogoutMutation } from "../features/auth/authApiSlice";
import { useSelector } from "react-redux";
import Modal from "../components/Modal";
import { selectMyInfo } from "../features/users/userSlice";
import {
  useDeleteUserMutation,
  useEditPictureMutation,
} from "../features/users/usersApiSlice";
import UpdateProfile from "../components/UpdateProfile";
import ChangePassword from "../components/ChangePassword";
import DarkModeSettings from "../components/DarkModeSettings";
import { ROUTES } from "../assets/data";
import Drawer from "../components/ui/drawer";
import { IoChevronForwardOutline } from "react-icons/io5";

const AccountSettings = () => {
  const user = useSelector(selectMyInfo);
  const navigate = useNavigate();
  const [sendLogout] = useSendLogoutMutation();
  const [deleteAccount] = useDeleteUserMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [selectedTab, setSelectedTab] = useState("personalInformation");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setModalOpen(true);
    }
  };

  const [editPicture, { isSuccess: isEpSuccess }] = useEditPictureMutation();

  useEffect(() => {
    if (user && user.userImage) {
      setProfileUrl(user.userImage);
    }
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem("persist");
    sendLogout();
    navigate(ROUTES.LOGIN);
  };

  const handleDelete = async () => {
    localStorage.removeItem("persist");
    try {
      await deleteAccount({ id: user._id });
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Failed to remove product from store:', error);
    }
  };

  const updateProfile = async ([imgFile]) => {
    if (imgFile instanceof File) {
      setProfileImage(imgFile);
      setProfileUrl(URL.createObjectURL(imgFile));

      const data = new FormData();
      data.append("userId", user._id);
      data.append("userImage", imgFile);

      try {
        const response = await editPicture(data).unwrap();
        console.log("Image updated successfully:", response);
        // Optionally update user state or show success message
      } catch (error) {
        console.error("Failed to update image:", error);
        // Handle error (e.g., show error message to user)
      }
    } else {
      console.error("Invalid image data received");
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedFile(null);
    // Reset the file input
    const fileInput = document.getElementById("profilePicInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    setIsDrawerOpen(false);
  };

  const NavigationLinks = () => (
    <>
      <span 
        className={`group pl-2 text-[.9rem] ${selectedTab === "personalInformation" ? "text-blue-600" : "text-slate-500"} cursor-pointer border-l border-slate-300`} 
        onClick={() => handleTabClick("personalInformation")}
      >
        Personal Information <span className="pl-1 group-hover:pl-2 pr-1 group-hover:pr-0 transition-all duration-300">→</span>
      </span>
      <span 
        className={`group pl-2 text-[.9rem] ${selectedTab === "darkMode" ? "text-blue-600" : "text-slate-500"} cursor-pointer border-l border-slate-300`} 
        onClick={() => handleTabClick("darkMode")}
      >
        Dark Mode <span className="pl-1 group-hover:pl-2 pr-1 group-hover:pr-0 transition-all duration-300">→</span>
      </span>
      <span 
        className="group pl-2 text-[.9rem] text-red-600 cursor-pointer border-l border-slate-300" 
        onClick={handleLogout}
      >
        Logout
      </span>
      <span 
        className="group pl-2 text-[.9rem] text-red-600 cursor-pointer border-l border-slate-300" 
        onClick={handleDelete}
      >
        Delete Account
      </span>
    </>
  );

  return (
    <div className="flex flex-col md:flex-row justify-start items-start flex-wrap px-[4%]">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsDrawerOpen(true)}
        className="md:hidden fixed top-[72px] left-4 z-30 p-2 bg-white rounded-lg shadow-md hover:bg-slate-50 transition-colors"
      >
        <IoChevronForwardOutline className="w-5 h-5" />
      </button>

      {/* Mobile Drawer */}
      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold mb-2">General Settings</p>
          <NavigationLinks />
        </div>
      </Drawer>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-col justify-start items-start flex-wrap p-4">
        <p className="text-xl font-semibold">General Settings</p>
        <NavigationLinks />
      </div>

      {/* Content Area */}
      <div className="w-full md:flex-1 mt-16 md:mt-0">
        {selectedTab === "personalInformation" && (
          <div className="flex justify-start items-start flex-wrap p-4 gap-4">
            {profileUrl && (
              <div className="relative">
                <img
                  src={profileUrl}
                  alt={`${user.firstName}'s profile`}
                  className="w-[150px] h-[150px] rounded-full border border-gray-700"
                />
                <label htmlFor="profilePicInput" className="cursor-pointer">
                  <span
                    className="material-icons absolute -bottom-3 left-0 right-0 m-auto w-fit p-[.35rem] rounded-full bg-black border border-white text-white text-[1.4rem] hover:text-[1.5rem] shadow-custom-shadow-main"
                    title="Change photo"
                  >
                    edit
                  </span>
                  <input
                    id="profilePicInput"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            )}
            {modalOpen && selectedFile && (
              <Modal
                updateAvatar={updateProfile}
                closeModal={closeModal}
                useFor="user"
                initialImage={selectedFile}
              />
            )}
            <div className="flex flex-col justify-start items-start flex-wrap gap-4">
              <UpdateProfile />
              <ChangePassword />
            </div>
          </div>
        )}
        {selectedTab === "darkMode" && <DarkModeSettings />}
      </div>
    </div>
  );
};

export default AccountSettings;
